import { formatDate, getCandidateFullName } from '@/utils';
import { Box, Stack, Flex, Text, Skeleton } from '@chakra-ui/react';
import { MeetingSummary } from '@/types/Meeting';
import { CalendarBlank, Clock } from '@phosphor-icons/react';
import { useMemo } from 'react';

interface RecentInterviewCardProps {
  meeting: MeetingSummary;
  onClick: () => void;
  isLoading?: boolean;
}

/**
 * Formats a time to a readable string with hour and minute
 */
const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('default', {
    hour: 'numeric',
    minute: 'numeric'
  });
};

/**
 * Formats duration in minutes to a readable string
 */
const formatDuration = (minutes: number): string => {
  return `${minutes} mins`;
};

export default function RecentInterviewCard({
  meeting,
  onClick,
  isLoading = false
}: RecentInterviewCardProps) {
  const meetingTime = useMemo(() => {
    return new Date(meeting.meeting_start_time * 1000);
  }, [meeting.meeting_start_time]);

  const durationInMinutes = useMemo(() => {
    return Math.round((meeting.timestamp.ended_at - meeting.timestamp.recorded_at) / 60);
  }, [meeting.timestamp.ended_at, meeting.timestamp.recorded_at]);

  if (isLoading) {
    return (
      <Box
        w="100%"
        bg="neutrals.50"
        border="1px solid"
        borderColor="neutrals.100"
        borderRadius="12px"
        p="24px"
      >
        <Stack gap="8px">
          <Skeleton height="24px" width="80%" />
          <Stack w="100%" fontSize="14px">
            <Skeleton height="20px" width="60%" />
            <Skeleton height="20px" width="70%" />
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      w="100%"
      bg="neutrals.50"
      border="1px solid"
      borderColor="neutrals.100"
      borderRadius="12px"
      p="24px"
      _hover={{ cursor: 'pointer', boxShadow: 'sm' }}
      transition="all 0.2s ease-in-out"
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`Interview with ${getCandidateFullName(
        meeting.candidate?.first_name || '',
        meeting.candidate?.last_name || ''
      )} on ${formatDate(meetingTime)}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick();
        }
      }}
    >
      <Flex justifyContent="space-between" w="100%" align="center">
        <Stack gap="8px">
          <Text fontSize="14px" color="neutrals.500">
            {meeting.candidate?.job.client.name} - {meeting.candidate?.job.job_title}
          </Text>
          <Text fontSize="18px" fontWeight="semibold" color="neutrals.900">
            {getCandidateFullName(
              meeting.candidate?.first_name || '',
              meeting.candidate?.last_name || ''
            )}{' '}
            - {meeting.title}
          </Text>
          <Stack w="100%" fontSize="14px" color="neutrals.700">
            <Flex gap="8px" align="center">
              <CalendarBlank size={20} color="#6B7280" aria-hidden="true" />
              {formatDate(meetingTime)}
              <Text>·</Text>
              <Clock size={20} color="#6B7280" aria-hidden="true" />
              {formatTime(meetingTime)}
              <Text>·</Text>
              <Text>{formatDuration(durationInMinutes)}</Text>
            </Flex>
          </Stack>
        </Stack>
      </Flex>
    </Box>
  );
}
